import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Seo from '../components/Seo';
import {
  Phone, Mail, MapPin, Clock, MessageCircle, User, Car, Shield,
  AlertCircle, CheckCircle, Send, ChevronDown, XCircle, ArrowRight, HeadphonesIcon,
} from 'lucide-react';
import HeaderSection from './HeaderSection';
import Footer from './Footer';
import ScrollToTop from './ScrollToTop';
import { useHoneypot } from '../hooks/useHoneypot';
import { isValidName, isValidPhone, isValidEmail } from '../lib/validate';

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-60px' },
  transition: { duration: 0.45, delay },
});

const CONTACT_INFO = [
  { icon: Phone, title: 'Phone Support', details: ['+91 8088257606'], description: 'Call us for immediate assistance, our team picks up fast.', href: 'tel:+918088257606', actionLabel: 'Call Now' },
  { icon: Mail, title: 'Email Support', details: ['contact@totowala.in'], description: "Drop us an email and we'll respond within a few hours.", href: 'mailto:contact@totowala.in', actionLabel: 'Send Email' },
  { icon: MapPin, title: 'Office Address', details: ['Beside R.E.O Office Gate', 'Bhagalpur, Bihar 812003'], description: "Come visit us during business hours, we'd love to meet you.", href: 'https://maps.app.goo.gl/Yh3ss1ZiS7HCpzPW9', actionLabel: 'Get Directions' },
  { icon: Clock, title: 'Support Hours', details: ['24/7 Emergency Support', 'Mon–Sun: Always Available'], description: "Round-the-clock support, we're never more than a message away.", href: null, actionLabel: null },
];

const SUPPORT_CATEGORIES = [
  { id: 'general', title: 'General', icon: MessageCircle, description: 'General questions about our services' },
  { id: 'rider', title: 'Rider', icon: User, description: 'Issues related to booking rides' },
  { id: 'driver', title: 'Driver', icon: Car, description: 'Help for our driver partners' },
  { id: 'safety', title: 'Safety', icon: Shield, description: 'Report safety concerns or incidents' },
  { id: 'technical', title: 'Technical', icon: AlertCircle, description: 'App problems and technical difficulties' },
];

const FAQ_ITEMS = [
  { question: "How does TotoWala's pricing model work?", answer: 'TotoWala currently offers zero commission as a launch offer, drivers keep the maximum possible share of their earnings. There are no platform fees or hidden charges. We are committed to always being the lowest-commission platform in the market.' },
  { question: 'How do I book a ride on TotoWala?', answer: 'Download our rider app from Google Play Store, create an account, enter your pickup and destination, select your ride type, and confirm your booking. A nearby driver will be assigned to you.' },
  { question: 'How can I become a TotoWala driver?', answer: 'Download our driver app, complete the registration process with required documents (driving license, vehicle registration, insurance), undergo verification, and start earning with the lowest commission in Bhagalpur.' },
  { question: 'What safety measures does TotoWala have?', answer: 'We conduct thorough background checks on all drivers, provide real-time ride tracking, 24/7 support, emergency SOS features, and driver photo verification for every ride.' },
  { question: 'In which cities is TotoWala available?', answer: 'TotoWala started in Bhagalpur, Bihar and is expanding across India.' },
  { question: 'How are ride fares calculated?', answer: "Our transparent pricing includes base fare, distance charges, and time charges. No hidden fees. You'll see the estimated fare before booking." },
];

const INFO_PANEL_POINTS = [
  'Dedicated support team for riders & drivers',
  'Technical experts available around the clock',
  'Hindi & English support',
  'Average response time under 2 hours',
];

const inputCls = 'w-full px-4 py-3 rounded-control bg-white dark:bg-dark-surface border border-line dark:border-dark-line text-ink dark:text-dark-ink text-sm placeholder:text-ink-muted outline-none focus:border-primary transition-colors disabled:opacity-50';

const Label = ({ children, required }) => (
  <label className="block text-xs font-semibold text-ink-soft dark:text-dark-ink-soft mb-2">
    {children}{required && <span className="text-red-500 ml-0.5">*</span>}
  </label>
);

const Contact = () => {
  const [activeTab, setActiveTab] = useState('general');
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', userType: 'rider', category: 'general', subject: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [submitMessage, setSubmitMessage] = useState('');
  const [openFaq, setOpenFaq] = useState(null);
  const { honeypotFieldProps, isSpam, spamCheckFields } = useHoneypot();

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isValidName(formData.name)) {
      setSubmitStatus('error');
      setSubmitMessage('Enter a valid name');
      return;
    }
    if (!isValidEmail(formData.email)) {
      setSubmitStatus('error');
      setSubmitMessage('Enter a valid email address');
      return;
    }
    if (formData.phone && !isValidPhone(formData.phone)) {
      setSubmitStatus('error');
      setSubmitMessage('Enter a valid phone number (10-15 digits)');
      return;
    }
    if (isSpam()) {
      setSubmitStatus('success');
      setSubmitMessage('Message sent! Our team will get back to you within 2 hours.');
      setFormData({ name: '', email: '', phone: '', userType: 'rider', category: 'general', subject: '', message: '' });
      setActiveTab('general');
      return;
    }
    setIsSubmitting(true);
    setSubmitStatus(null);
    setSubmitMessage('');
    try {
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...formData, ...spamCheckFields() }),
      });
      if (!res.ok) throw new Error(`submit failed: ${res.status}`);
      setSubmitStatus('success');
      setSubmitMessage('Message sent! Our team will get back to you within 2 hours.');
      setFormData({ name: '', email: '', phone: '', userType: 'rider', category: 'general', subject: '', message: '' });
      setActiveTab('general');
    } catch (error) {
      console.error('Contact form submit failed:', error);
      setSubmitStatus('error');
      setSubmitMessage('Failed to send message. Please email us directly at contact@totowala.in');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Seo
        title="Contact TotoWala - Get Support & Help | India Ride Booking"
        description="Contact TotoWala's support team for help with ride bookings, driver queries, or feedback. Reach us via phone, email, or our online form. Available 24/7 across India."
        canonical="/contact"
        schemaMarkup={{
          '@context': 'https://schema.org',
          '@type': 'ContactPage',
          name: 'Contact TotoWala',
          description: 'Get in touch with TotoWala for support, driver registration, or general enquiries.',
          url: 'https://totowala.in/contact',
          publisher: { '@type': 'Organization', name: 'TotoWala Technologies Private Limited', telephone: '+91-8088257606', url: 'https://totowala.in' },
        }}
      />
      <div className="min-h-screen bg-white dark:bg-dark-bg">
        <HeaderSection />

        {/* ── Hero ── */}
        <section className="bg-surface-alt dark:bg-dark-surface-alt py-12 lg:py-14 px-4">
          <div className="max-w-[760px] mx-auto text-center">
            <motion.h1
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45 }}
              className="font-display text-3xl sm:text-4xl font-semibold leading-tight text-ink dark:text-dark-ink mb-4"
            >
              Talk to our team
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.06 }}
              className="text-base text-ink-soft dark:text-dark-ink-soft leading-relaxed max-w-lg mx-auto mb-7"
            >
              Whether you're a rider, driver, or partner, our support team is ready to help 24/7. No long waits, no bots, just real people.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.1 }}
              className="flex flex-col sm:flex-row gap-3 justify-center"
            >
              <a href="tel:+918088257606" className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full font-semibold text-sm text-white bg-primary hover:bg-primary-dark transition-colors">
                <Phone size={16} /> Call Now
              </a>
              <a href="mailto:contact@totowala.in" className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full font-semibold text-sm text-ink dark:text-dark-ink border border-line dark:border-dark-line hover:border-primary transition-colors">
                <Mail size={16} /> Email Us
              </a>
            </motion.div>
          </div>
        </section>

        {/* ── Contact info cards ── */}
        <section className="bg-white dark:bg-dark-bg py-12 lg:py-14 px-4">
          <div className="max-w-[1200px] mx-auto">
            <h2 className="font-display text-2xl sm:text-3xl font-semibold text-center text-ink dark:text-dark-ink mb-8">
              Contact Information
            </h2>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {CONTACT_INFO.map(({ icon: Icon, title, details, description, href, actionLabel }, i) => (
                <motion.div key={title} {...fadeUp(i * 0.06)} className="p-5 rounded-card bg-white dark:bg-dark-surface border border-line dark:border-dark-line flex flex-col">
                  <div className="w-10 h-10 rounded-control bg-primary-tint dark:bg-primary/10 border border-primary/25 flex items-center justify-center mb-4">
                    <Icon size={18} className="text-primary" />
                  </div>
                  <h3 className="text-sm font-bold text-ink dark:text-dark-ink mb-1.5">{title}</h3>
                  {details.map((d) => (
                    <p key={d} className="text-xs font-semibold text-primary leading-relaxed">{d}</p>
                  ))}
                  <p className="text-xs text-ink-muted leading-relaxed mt-2 mb-4 flex-1">{description}</p>
                  {actionLabel && href && (
                    <a
                      href={href} target={href.startsWith('http') ? '_blank' : undefined} rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-xs font-semibold text-primary w-fit"
                    >
                      {actionLabel} <ArrowRight size={12} />
                    </a>
                  )}
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* ── Form ── */}
        <section className="bg-surface-alt dark:bg-dark-surface-alt py-12 lg:py-14 px-4">
          <div className="max-w-[1200px] mx-auto">
            <h2 className="font-display text-2xl sm:text-3xl font-semibold text-center text-ink dark:text-dark-ink mb-8">
              Send Us a Message
            </h2>

            <div className="flex flex-col lg:flex-row gap-8 items-start">

              {/* Left: info panel */}
              <motion.div {...fadeUp(0.06)} className="w-full lg:w-2/5 shrink-0">
                <div className="rounded-card bg-primary p-7 mb-4">
                  <div className="w-11 h-11 rounded-control bg-white/15 border border-white/20 flex items-center justify-center mb-5">
                    <HeadphonesIcon size={22} className="text-white" />
                  </div>
                  <h3 className="font-display text-xl font-semibold text-white mb-2">We're here to help</h3>
                  <p className="text-sm text-white/75 leading-relaxed mb-5">
                    Fill out the form and our team will get back to you within 2 hours.
                  </p>
                  <div className="flex flex-col gap-2.5">
                    {INFO_PANEL_POINTS.map((text) => (
                      <div key={text} className="flex items-center gap-2.5">
                        <CheckCircle size={14} className="text-white/80 shrink-0" />
                        <span className="text-sm text-white/85">{text}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex flex-col gap-2.5">
                  {[
                    { icon: Phone, label: '+91 8088257606', href: 'tel:+918088257606' },
                    { icon: Mail, label: 'contact@totowala.in', href: 'mailto:contact@totowala.in' },
                  ].map(({ icon: Icon, label, href }) => (
                    <a key={label} href={href} className="flex items-center gap-3 px-4 py-3.5 rounded-control bg-white dark:bg-dark-surface border border-line dark:border-dark-line">
                      <div className="w-8 h-8 rounded-control bg-primary-tint dark:bg-primary/10 flex items-center justify-center shrink-0">
                        <Icon size={15} className="text-primary" />
                      </div>
                      <span className="text-sm font-semibold text-ink dark:text-dark-ink">{label}</span>
                      <ArrowRight size={14} className="text-ink-muted ml-auto" />
                    </a>
                  ))}
                </div>
              </motion.div>

              {/* Right: form */}
              <motion.div {...fadeUp(0.1)} className="w-full lg:flex-1 min-w-0">
                <div className="grid grid-cols-5 gap-2 mb-4">
                  {SUPPORT_CATEGORIES.map(({ id, title, icon: Icon }) => {
                    const active = activeTab === id;
                    return (
                      <button
                        key={id}
                        onClick={() => { setActiveTab(id); setFormData((prev) => ({ ...prev, category: id })); }}
                        className={`flex flex-col items-center gap-1.5 py-3 px-1 rounded-control border transition-colors ${
                          active ? 'bg-primary-tint dark:bg-primary/10 border-primary text-primary' : 'bg-white dark:bg-dark-surface border-line dark:border-dark-line text-ink-soft dark:text-dark-ink-soft'
                        }`}
                      >
                        <Icon size={16} />
                        <span className={`text-[11px] text-center leading-tight ${active ? 'font-bold' : 'font-medium'}`}>{title}</span>
                      </button>
                    );
                  })}
                </div>

                <AnimatePresence mode="wait">
                  <motion.div
                    key={activeTab}
                    initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }}
                    className="px-4 py-2.5 rounded-control bg-primary-tint dark:bg-primary/10 mb-5"
                  >
                    <span className="text-sm text-primary font-medium">
                      {SUPPORT_CATEGORIES.find((c) => c.id === activeTab)?.description}
                    </span>
                  </motion.div>
                </AnimatePresence>

                <div className="rounded-card bg-white dark:bg-dark-surface border border-line dark:border-dark-line p-6 sm:p-7">
                  {submitStatus === 'success' && (
                    <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="mb-5 px-4 py-3.5 bg-teal-tint dark:bg-teal/10 border border-teal/25 rounded-control flex items-center gap-3">
                      <CheckCircle size={18} className="text-teal-dark dark:text-teal shrink-0" />
                      <span className="text-sm text-teal-dark dark:text-teal">{submitMessage}</span>
                    </motion.div>
                  )}
                  {submitStatus === 'error' && (
                    <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="mb-5 px-4 py-3.5 bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/25 rounded-control flex items-center gap-3">
                      <XCircle size={18} className="text-red-600 dark:text-red-400 shrink-0" />
                      <span className="text-sm text-red-700 dark:text-red-400">{submitMessage}</span>
                    </motion.div>
                  )}

                  <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                    <input {...honeypotFieldProps} type="text" placeholder="Company" />
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <Label required>Full Name</Label>
                        <input className={inputCls} type="text" name="name" value={formData.name} onChange={handleInputChange} required disabled={isSubmitting} placeholder="Your full name" />
                      </div>
                      <div>
                        <Label required>Email Address</Label>
                        <input className={inputCls} type="email" name="email" value={formData.email} onChange={handleInputChange} required disabled={isSubmitting} placeholder="your@email.com" />
                      </div>
                    </div>

                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <Label>Phone Number</Label>
                        <input className={inputCls} type="tel" inputMode="tel" pattern="\+?[0-9]{10,15}" name="phone" value={formData.phone} onChange={handleInputChange} disabled={isSubmitting} placeholder="+91 XXXXX XXXXX" />
                      </div>
                      <div>
                        <Label required>I am a</Label>
                        <select className={`${inputCls} cursor-pointer`} name="userType" value={formData.userType} onChange={handleInputChange} required disabled={isSubmitting}>
                          <option value="rider">Rider</option>
                          <option value="driver">Driver</option>
                          <option value="partner">Business Partner</option>
                          <option value="other">Other</option>
                        </select>
                      </div>
                    </div>

                    <div>
                      <Label required>Subject</Label>
                      <input className={inputCls} type="text" name="subject" value={formData.subject} onChange={handleInputChange} required disabled={isSubmitting} placeholder="Brief description of your inquiry" />
                    </div>

                    <div>
                      <Label required>Message</Label>
                      <textarea className={`${inputCls} resize-y min-h-[130px] leading-relaxed`} name="message" value={formData.message} onChange={handleInputChange} required disabled={isSubmitting} rows={5} placeholder="Please provide detailed information about your inquiry" />
                    </div>

                    <button
                      type="submit" disabled={isSubmitting}
                      className="w-full flex items-center justify-center gap-2.5 py-3.5 rounded-control bg-primary hover:bg-primary-dark text-white font-bold text-sm transition-colors disabled:opacity-60"
                    >
                      {isSubmitting ? (
                        <><div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />Sending...</>
                      ) : (
                        <><Send size={16} />Send Message</>
                      )}
                    </button>
                  </form>
                </div>
              </motion.div>

            </div>
          </div>
        </section>

        {/* ── FAQ ── */}
        <section className="bg-white dark:bg-dark-bg py-12 lg:py-14 px-4">
          <div className="max-w-[1200px] mx-auto">
            <h2 className="font-display text-2xl sm:text-3xl font-semibold text-center text-ink dark:text-dark-ink mb-8">
              Frequently Asked Questions
            </h2>

            <div className="grid sm:grid-cols-2 gap-3 max-w-4xl mx-auto">
              {FAQ_ITEMS.map((item, i) => {
                const open = openFaq === i;
                return (
                  <motion.div key={item.question} {...fadeUp(Math.floor(i / 2) * 0.06)} className="h-fit">
                    <div className={`rounded-card bg-white dark:bg-dark-surface border overflow-hidden transition-colors ${open ? 'border-primary' : 'border-line dark:border-dark-line'}`}>
                      <button onClick={() => setOpenFaq(open ? null : i)} className="w-full flex items-start justify-between gap-3 p-5 text-left">
                        <span className={`text-sm font-semibold leading-relaxed ${open ? 'text-primary' : 'text-ink dark:text-dark-ink'}`}>{item.question}</span>
                        <motion.div animate={{ rotate: open ? 180 : 0 }} transition={{ duration: 0.2 }} className="shrink-0 mt-0.5">
                          <ChevronDown size={16} className={open ? 'text-primary' : 'text-ink-muted'} />
                        </motion.div>
                      </button>
                      <AnimatePresence initial={false}>
                        {open && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.25 }} className="overflow-hidden"
                          >
                            <div className="px-5 pb-5 text-sm text-ink-soft dark:text-dark-ink-soft leading-relaxed border-t border-line dark:border-dark-line pt-4">
                              {item.answer}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  </motion.div>
                );
              })}
            </div>

            <p className="text-center text-sm text-ink-muted mt-7">
              Still have questions?{' '}
              <a href="mailto:contact@totowala.in" className="text-primary font-semibold">Email us directly →</a>
            </p>
          </div>
        </section>

        {/* ── Emergency ── */}
        <section className="bg-ink py-12 lg:py-14 px-4">
          <div className="max-w-[760px] mx-auto flex flex-col sm:flex-row items-center gap-6 sm:gap-8">
            <div className="w-16 h-16 rounded-full bg-white/10 border border-white/15 flex items-center justify-center shrink-0">
              <AlertCircle size={28} className="text-accent" />
            </div>
            <div className="text-center sm:text-left">
              <span className="text-xs font-bold uppercase tracking-wider text-white/50">24/7 Emergency Line</span>
              <h2 className="font-display text-xl sm:text-2xl font-semibold text-white mt-1.5 mb-2">Need immediate help?</h2>
              <p className="text-sm text-white/65 leading-relaxed mb-4 max-w-md">
                For urgent safety concerns or emergencies during your ride, contact our 24/7 emergency helpline immediately.
              </p>
              <a
                href="tel:+918088257606"
                className="inline-flex items-center gap-2.5 px-6 py-3 rounded-full font-bold text-sm bg-white text-ink hover:bg-white/90 transition-colors"
              >
                <Phone size={16} /> +91 8088257606
              </a>
            </div>
          </div>
        </section>

        <Footer />
        <ScrollToTop />
      </div>
    </>
  );
};

export default Contact;
